/**
 * PostHog Analytics — centralised init & helpers.
 *
 * Env vars (set in .env):
 *   VITE_POSTHOG_KEY  — PostHog project API key
 *   VITE_POSTHOG_HOST — PostHog ingest host (default: https://us.i.posthog.com)
 */
import posthog from 'posthog-js';

const POSTHOG_KEY = import.meta.env.VITE_POSTHOG_KEY;
const POSTHOG_HOST = import.meta.env.VITE_POSTHOG_HOST || 'https://us.i.posthog.com';

/**
 * Initialise PostHog. Call once at app boot (main.jsx).
 * No-ops gracefully when the API key is missing so dev/test environments
 * don't break.
 */
export function initPostHog() {
  if (!POSTHOG_KEY) {
    console.warn('[PostHog] VITE_POSTHOG_KEY not set — analytics disabled.');
    return;
  }

  posthog.init(POSTHOG_KEY, {
    api_host: POSTHOG_HOST,
    capture_pageview: false,   // we track SPA page views manually
    capture_pageleave: true,
    autocapture: true,
  });
}

/**
 * Identify the logged-in user. Call after login / session verify.
 */
export function identifyUser(user) {
  if (!POSTHOG_KEY || !user) return;

  posthog.identify(String(user.id), {
    email: user.email,
    name: user.name,
    role: user.role,
    company_id: user.company_id,
  });

  // Group analytics by company
  if (user.company_id) {
    posthog.group('company', String(user.company_id));
  }
}

/**
 * Reset identity on logout so the next session starts anonymous.
 */
export function resetPostHog() {
  if (!POSTHOG_KEY) return;
  posthog.reset();
}

/**
 * Capture a manual page view (for SPA route changes).
 */
export function capturePageView() {
  if (!POSTHOG_KEY) return;
  posthog.capture('$pageview');
}

export default posthog;
